<template>
  <div>
    <message :messages="messages"></message>
    <v-card>
      <v-card-title>
        <h3 class="headline">{{labels.clmItems}} for {{claimForm.selectedClaimant.clmtName}}</h3>
        <v-spacer></v-spacer>
        <v-btn color="secondary" v-if="!clmItemSection" @click.prevent="doAddClaimItem">
          Add Claim Item
          <v-icon right>mdi-playlist-plus</v-icon>
        </v-btn>
      </v-card-title>
      <v-data-table :headers="clmItemColumns" :items="claimForm.clmItems" class="elevation-1" :no-data-text="noDataText" hide-actions>
        <template slot="items" slot-scope="props">
          <td>{{props.index + 1}}</td>
          <td>{{props.item.nol1}}</td>
          <td>{{props.item.nol2}}</td>
          <td>{{props.item.lossDesc1}}</td>
          <td class="text-xs-right">
            <table-action :rowData="props" review :query="false" :remove="!clmItemSection" @remove="doPromptRemove(props)" @review="doEditClaimItem(props)" :loading="loading"></table-action>
          </td>
        </template>
      </v-data-table>
    </v-card>
    <v-card class="mt-3" v-if="clmItemSection" id="clmItemCard">
      <v-card-title>
        <div class="body-2">{{labels.clmItemInfo}}</div>
      </v-card-title>
      <v-card-text>
        <v-container fluid grid-list-lg>
          <v-layout row wrap>
            <v-flex lg6 xs12>
              <v-autocomplete :items="claimParam.nol1.data" v-model="clmItem.nol1" :label="labels.nol1" item-text="label" item-value="value" data-vv-name="nol1" :data-vv-as="labels.nol1" :error-messages="errors.collect('nol1')" @input="doNol1" :loading="nol1Loading" clearable></v-autocomplete>
            </v-flex>
            <v-flex lg6 xs12>
              <v-autocomplete :items="claimParam.nol2" v-model="clmItem.nol2" :label="clmItem.nol1? labels.nol2Mand:labels.nol2" item-text="label" item-value="value" data-vv-name="nol2" :data-vv-as="labels.nol2" :error-messages="errors.collect('nol2')" :loading="nol2Loading" clearable v-validate="nol2Mandatory"></v-autocomplete>
            </v-flex>
            <v-flex xs12>
              <v-text-field v-model="clmItem.lossDesc1" :label="labels.lossDesc1" type="tel" :error-messages="errors.collect('lossDesc1')" data-vv-name="lossDesc1" :data-vv-as="labels.lossDesc1" v-validate="'required|entry|max:35'"></v-text-field>
            </v-flex>
            <v-flex xs12>
              <v-text-field v-model="clmItem.lossDesc2" :label="labels.lossDesc2" type="tel" :error-messages="errors.collect('lossDesc2')" data-vv-name="lossDesc2" :data-vv-as="labels.lossDesc2" v-validate="'entry|max:35'"></v-text-field>
            </v-flex>
            <v-flex xs12>
              <v-text-field v-model="clmItem.lossDesc3" :label="labels.lossDesc3" type="tel" :error-messages="errors.collect('lossDesc3')" data-vv-name="lossDesc3" :data-vv-as="labels.lossDesc3" v-validate="'entry|max:35'"></v-text-field>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>
      <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" flat="flat" @click.native="clmItemSection = false">Cancel</v-btn>
          <v-btn color="primary" flat="flat" type="submit" @click="doSaveClaimItem">Next</v-btn>
        </v-card-actions>
    </v-card>
    <confirm v-model="promptRemoveClaimItem" @ok="doRemoveClaimItem">
      All payment items under this claim item will be deleted.
    </confirm>
    <v-layout row wrap class="pt-3">
      <v-btn round color="primary" @click.prevent="doBack">
        <v-icon left dark>mdi-chevron-left</v-icon>
        Back
      </v-btn>
    </v-layout>
  </div>
</template>

<script>
import { CLAIMS_CLAIMDETAIL_LIFECYCLE, CLAIMS_CLAIMDETAIL_CLAIMITEM, CLAIMS_CLAIMDETAIL_PARAM, CLAIMS_CLAIMDETAIL_PARAM_NOL1 } from '@/store/types'
import MessageMixin from '@/mixins/message'
import * as TableConst from '@/mixins/table'
import clone from 'clone'
import { formatDisplayDate } from '@/utils/date'
import { SaveClaimMixin } from '../saveClaimMixin'
import { expandURI } from '@/utils/ajax'
import { extractMenu } from '@/utils/extract'

export default {
  data() {
    return {
      labels: {
        clmItems: 'Claim Items',
        clmItemInfo: 'Claim Item Information',
        claimant: 'Claimant',
        nol1: 'Nature of Loss 1',
        nol2: 'Nature of Loss 2',
        nol2Mand: 'Nature of Loss 2*',
        lossDesc1: 'Loss Description*',
        lossDesc2: '',
        lossDesc3: ''
      },
      claimForm: {
        clmItems: [],
        selectedClaimant: undefined,
        selectedClmItem: undefined
      },
      clmItem: {
        clmtNo: undefined,
        clmItemNo: undefined,
        lossDesc1: undefined,
        lossDesc2: undefined,
        lossDesc3: undefined,
        nol1: undefined,
        nol2: undefined,
        clmReserves: []
      },
      clmItemSection: false,
      promptRemoveClaimItem: false,
      selectedClaimItem: undefined,
      nol1Loading: false,
      nol2Loading: false
    }
  },
  computed: {
    globalCommonForm() {
      return this.$store.state.claims.claimDetail.commonForm
    },
    globalClaimForm() {
      return this.$store.state.claims.claimDetail.claimForm
    },
    clmItemColumns() {
      return [
        { text: 'No.', align: 'left', sortable: false, value: 'clmItemNo' },
        { text: 'NOL1', sortable: false, value: 'nol1' },
        { text: 'NOL2', sortable: false, value: 'nol2' },
        { text: 'Loss Description', sortable: false, value: 'lossDesc1' },
        TableConst.tableActionHeader
      ]
    },
    claimParam() {
      return this.$store.state.claims.claimDetail.claimParam
    },
    nol2Mandatory() {
      let result = ''
      if (this.clmItem.nol1) {
        result = 'required'
      }
      return result
    }
  },
  methods: {
    doInit() {
      // Default value from vuex store 1st
      this.getNol1()
    },
    doAddClaimItem() {
      // Init the ClaimItem details
      this.resetValidator()
      if (this.clmItemSection !== true) {
        this.clmItem.clmtNo = this.claimForm.selectedClaimant.clmtNo
        this.clmItem.clmItemNo = undefined
        this.clmItem.nol1 = undefined
        this.clmItem.nol2 = undefined
        this.clmItem.lossDesc1 = undefined
        this.clmItem.lossDesc2 = undefined
        this.clmItem.lossDesc3 = undefined
        this.clmItem.clmReserves = []
        this.clmItemSection = true
        this.$nextTick(() => {
          this.$vuetify.goTo('#clmItemCard')
        })
      }
    },
    doPromptRemove(props) {
      this.selectedClaimItem = props
      this.promptRemoveClaimItem = true
    },
    doRemoveClaimItem() {
      if (this.selectedClaimItem && this.selectedClaimItem.index !== null) {
        this.claimForm.clmItems.splice(this.selectedClaimItem.index, 1)
      }
    },
    doEditClaimItem(props) {
      // Init the ClaimItem details
      this.clmItem.clmtNo = props.item.clmtNo
      this.clmItem.clmItemNo = props.index
      this.clmItem.nol1 = props.item.nol1
      if (this.clmItem.nol1) {
        this.doNol1(props.item.nol2)
      }
      this.clmItem.nol2 = props.item.nol2

      this.clmItem.lossDesc1 = props.item.lossDesc1
      this.clmItem.lossDesc2 = props.item.lossDesc2
      this.clmItem.lossDesc3 = props.item.lossDesc3
      this.clmItem.clmReserves = props.item.clmReserves
      this.resetValidator()
      this.clmItemSection = true
      this.$nextTick(() => {
        this.$vuetify.goTo('#clmItemCard')
      })
    },
    async getNol1() {
      const apiURI = this.$store.state.claims.claimDetail.menu.nol1
      if (apiURI) {
        this.nol1Loading = true
        try {
          const expand = {
            busCls: this.globalCommonForm.busCls
          }
          const result = await this.$http.get(expandURI(apiURI, expand))
          if (result.data) {
            const { data } = result
            const menu = extractMenu(data._links)
            Object.assign(data, {
              menu: menu
            })
            this.$store.commit(CLAIMS_CLAIMDETAIL_PARAM_NOL1, data)
            this.nol1Loading = false
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
          this.nol1Loading = false
        }
      } else {
        this.$snotify.error('Invalid API URL')
      }
    },
    doNol1(props) {
      this.getNol2(props)
    },
    async getNol2(props) {
      const apiURI = this.claimParam.nol1.menu.nol2
      if (apiURI) {
        this.nol2Loading = true
        try {
          const expand = {
            nol1: this.clmItem.nol1
          }
          const result = await this.$http.get(expandURI(apiURI, expand))
          if (result.data) {
            const { data } = result
            const commitResult = {
              nol2: data.data
            }
            this.$store.commit(CLAIMS_CLAIMDETAIL_PARAM, commitResult)
            this.nol2Loading = false
            // Check if the current nol2 value is still valid
            if (props && this.claimParam.nol2 && this.claimParam.nol2.length > 0) {
              const validNol2 = this.claimParam.nol2.filter(nol2 => nol2.value === props)

              if (!validNol2 || validNol2.length === 0) {
                this.clmItem.nol2 = undefined
              } else {
                this.clmItem.nol2 = props
              }
            } else {
              this.clmItem.nol2 = undefined
            }
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
          this.nol2Loading = false
        }
      } else {
        this.$snotify.error('Invalid API URL')
      }
    },
    doBack() {
      this.$store.commit(CLAIMS_CLAIMDETAIL_LIFECYCLE, 1)
      this.$router.push({ name: 'claimDetail' })
    },
    doSaveClaimItem() {
      this.resetMessages()
      this.$validator.validateAll().then(result => {
        if (result) {
          if (this.clmItem.clmItemNo === undefined) {
            // New ClaimItem
            this.clmItem.clmItemNo = this.claimForm.clmItems.length
            this.claimForm.clmItems.push(clone(this.clmItem))
            this.clmItemSection = false
          } else {
            // Update ClaimItem
            this.$set(this.claimForm.clmItems, this.clmItem.clmItemNo, clone(this.clmItem))
            this.clmItemSection = false
          }
          this.claimForm.selectedClmItem = this.clmItem
          this.$store.commit(CLAIMS_CLAIMDETAIL_CLAIMITEM, this.claimForm)
          this.$store.commit(CLAIMS_CLAIMDETAIL_LIFECYCLE, 3)
          this.$router.push({ name: 'claimDetailReserve' })
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doPartialSave() {
      // No mandatory validation here. Commit whatever thats on screen and do neccesary actions before saving
      // Must return true/false
      try {
        this.$store.commit(CLAIMS_CLAIMDETAIL_CLAIMITEM, this.claimForm)
        await this.$store.dispatch('saveClaim')
        return true
      } catch (err) {
        return false
      }
    },
    formatDisplayDate(xDate) {
      return formatDisplayDate(xDate)
    }
  },
  beforeMount() {
    // Check quote lifecycle (might be using back link or url)
    if (this.$store.state.claims.claimDetail.claimLifeCycle !== 2) {
      this.$store.commit(CLAIMS_CLAIMDETAIL_LIFECYCLE, 2)
    }
    this.doInit()
  },
  created() {
    this.claimForm.selectedClaimant = this.globalClaimForm.selectedClaimant
    if (this.claimForm.selectedClaimant.clmItems && this.claimForm.selectedClaimant.clmItems.length > 0) {
      this.claimForm.clmItems = clone(this.claimForm.selectedClaimant.clmItems)
    }
  },
  mixins: [
    MessageMixin,
    TableConst.default,
    SaveClaimMixin
  ]
}
</script>

<style lang="scss">
</style>
